import { z } from 'zod';
import { isoDatetime, paginationQuery, uuid } from './common.js';

// ─── Public shape returned by GET endpoints ────────────────────────────────
// Hydrates the joined unit (block / number / label) and creator name so the
// admin UI doesn't have to do separate fetches per row.
export const publicInviteCode = z.object({
  id: uuid,
  cluster_id: uuid,
  unit_id: uuid,
  code: z.string(),                       // random 6-digit number, e.g. "048391"
  max_uses: z.number().int().positive(),
  uses_count: z.number().int().nonnegative(),
  is_active: z.boolean(),
  expires_at: isoDatetime.nullable(),
  created_by_user_id: uuid,
  created_at: isoDatetime,
  // Joins (may be null if the related row was deleted/orphaned)
  unit_block: z.string().nullable(),
  unit_number: z.string().nullable(),
  unit_label: z.string().nullable(),
  created_by_name: z.string().nullable(),
  // Derived status the server computes so the UI doesn't need clock-sync:
  //   ACTIVE        — is_active && uses_count < max_uses && (expires_at == null || expires_at > now)
  //   EXHAUSTED     — uses_count >= max_uses (regardless of is_active)
  //   EXPIRED       — expires_at <= now
  //   REVOKED       — is_active = false
  status: z.enum(['ACTIVE', 'EXHAUSTED', 'EXPIRED', 'REVOKED']),
});
export type PublicInviteCode = z.infer<typeof publicInviteCode>;

// ─── Create one invite code ────────────────────────────────────────────────
// The admin composes the unit's address parts per the cluster's scheme (street
// optional, block toggleable, number always required). The server validates
// against the scheme, then finds-or-creates that unit in the cluster and issues
// the code for it — so when the resident enters the code they're linked straight
// to that unit. Codes always expire 24 hours after creation (server-stamped
// `expires_at`), and the whole household (up to `max_uses`) must sign up within
// that window. `street`/`block` are optional in the wire shape; presence is
// enforced server-side via checkAddressParts against the cluster scheme.
// Fixed household size: how many accounts one code allows AND the per-unit
// resident cap that drives the capacity warning. Static (not per-cluster).
export const MAX_RESIDENTS_PER_UNIT = 3;

export const createInviteCodeRequest = z.object({
  street: z.string().max(20).trim().optional(),
  block: z.string().max(20).trim().optional(),
  number: z.string().min(1).max(20).trim(),
  max_uses: z.number().int().min(1).max(50).default(MAX_RESIDENTS_PER_UNIT),
  // Set true to proceed past the "unit already at its household cap" warning
  // (ERR_UNIT_AT_CAPACITY). The admin confirms in a pop-up and we re-submit.
  confirm_over_capacity: z.boolean().optional(),
  // Set true to replace the unit's existing active code (delete it, issue a new
  // one) after the admin confirms in the "already has a code" pop-up.
  replace_existing: z.boolean().optional(),
});
export type CreateInviteCodeRequest = z.infer<typeof createInviteCodeRequest>;

// ─── Bulk-create codes ─────────────────────────────────────────────────────
// Two modes, mutually exclusive:
//   { unit_ids: [...]      }  — explicit list
//   { for_unlinked_units }    — every unit in the cluster with no active code
// All generated codes expire 24h after creation (server-stamped).
export const bulkCreateInviteCodesRequest = z.union([
  z.object({
    unit_ids: z.array(uuid).min(1).max(500),
    max_uses: z.number().int().min(1).max(50).default(2),
  }),
  z.object({
    for_unlinked_units: z.literal(true),
    max_uses: z.number().int().min(1).max(50).default(2),
  }),
]);
export type BulkCreateInviteCodesRequest = z.infer<
  typeof bulkCreateInviteCodesRequest
>;

export const bulkCreateInviteCodesResponse = z.object({
  codes: z.array(publicInviteCode),
  skipped_unit_ids: z.array(uuid), // units already had an active code
});
export type BulkCreateInviteCodesResponse = z.infer<
  typeof bulkCreateInviteCodesResponse
>;

// ─── List query ────────────────────────────────────────────────────────────
export const listInviteCodesQuery = paginationQuery.extend({
  unit_id: uuid.optional(),
  status: z.enum(['ACTIVE', 'EXHAUSTED', 'EXPIRED', 'REVOKED']).optional(),
});
export type ListInviteCodesQuery = z.infer<typeof listInviteCodesQuery>;

export const listInviteCodesResponse = z.object({
  items: z.array(publicInviteCode),
  next_cursor: z.string().nullable(),
});
export type ListInviteCodesResponse = z.infer<typeof listInviteCodesResponse>;

// ─── Delete ────────────────────────────────────────────────────────────────
// DELETE /admin/invite-codes/:id hard-deletes the row (204, no body). The admin
// generates a fresh code if the resident needs access again. Replaces the old
// soft "revoke" — see
// docs/superpowers/specs/2026-06-27-unit-residents-and-invite-code-delete-design.md.
